import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { format } from 'date-fns';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './constants/permissions';
import { canAddResource, getEmployeeCapacity } from '../packages/utils/packageLimits';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext';

import PendingInvitesDialog from './invitations/components/PendingInvitesDialog';
import EmployeeSearchHeader from './components/employee-management/search/EmployeeSearchHeader';
import EmployeeManagementBottomNav from './components/employee-management/mobile/EmployeeManagementBottomNav';
import EmployeeMobileFilterDrawers from './components/employee-management/mobile/EmployeeMobileFilterDrawers';
import EmployeeGallery from './components/employee-management/EmployeeGallery';
import MobileManagementPagination from '../components/management-list/mobile/MobileManagementPagination';
import EmployeeListView from './components/employee-management/EmployeeListView';
import { getCompanyEmployeeUrlSegment } from '../shared/employees/companyEmployeeRouting';
import { listEmployees, deleteEmployee } from '../api/companyRecords';
import { patchCompany } from '../api/company/company';
import '../components/clients/ClientManagement.css';
import './styles/EmployeeManagementPage.css';
import '../shared/management-layout/ManagementSplitLayout.css';

const isOwnerRole = (role) => (role || '').toLowerCase() === 'owner';
const isManagerRole = (role) => (role || '').toLowerCase() === 'manager';

function useSmDown() {
  const [isSmDown, setIsSmDown] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches,
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 600px)');
    const onChange = () => setIsSmDown(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isSmDown;
}

export default function EmployeeManagement() {
  const { user, companyData, updateCompanyData } = useAuth();
  const { mode } = useTheme();
  const isDarkMode = mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilterId, setRoleFilterId] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const isSmDown = useSmDown();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('gallery');
  const effectiveViewMode = isSmDown ? 'gallery' : viewMode;
  const itemsPerPage = effectiveViewMode === 'gallery' ? 24 : 10;
  const [openPendingInvites, setOpenPendingInvites] = useState(false);
  const [mobileRoleFilterOpen, setMobileRoleFilterOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { hasPermission } = usePermissions();
  const canViewEmployees = hasPermission(PERMISSIONS.EMPLOYEES.VIEW);
  const canCreateEmployee = hasPermission(PERMISSIONS.EMPLOYEES.CREATE);
  const canEditEmployee = user?.role === 'owner' && hasPermission(PERMISSIONS.EMPLOYEES.EDIT);
  const canDeleteEmployee = hasPermission(PERMISSIONS.EMPLOYEES.DELETE);
  const canExport = hasPermission(PERMISSIONS.EMPLOYEES.EXPORT);
  const { setNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const snack = location.state?.employeeListSnack;
    if (!snack?.message) return;
    const sev = snack.severity === 'warning' || snack.severity === 'error' ? snack.severity : 'success';
    setNotification({ open: true, message: snack.message, severity: sev });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const ownerId = companyData?.ownerId;
  const companyId = companyData?.id;

  const fetchEmployees = useCallback(async () => {
    if (!companyId || !ownerId || !user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError('Load timed out. Please refresh the page.');
    }, 15000);
    try {
      const employeesList = await listEmployees({ companyId });
      const ownerExists = employeesList.some((emp) => emp.id === user.uid || emp.email === user.email);
      let allEmployees;
      if (!ownerExists) {
        allEmployees = [
          {
            id: user.uid,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            role: 'owner',
            slug: '',
            createdAt: user.createdAt || null,
            status: user.status || 'active',
            photoURL: user.photoURL ?? null,
            type: 'company_admin',
            companyId,
          },
          ...employeesList,
        ];
      } else {
        allEmployees = employeesList;
      }

      setEmployees(allEmployees);
      clearTimeout(timeoutId);
      setLoading(false);

      patchCompany({
        companyId,
        company: {
          resourceCounts: {
            employees: allEmployees.length,
            employeesCount: allEmployees.length,
          },
          employeesCount: allEmployees.length,
        },
      })
        .then((company) => {
          updateCompanyData?.({ ...(companyData || {}), ...company });
        })
        .catch((err) => {
          console.warn('Background employee count update failed:', err);
        });
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Error fetching employees:', err);
      setError('Failed to fetch employees. Please try again.');
      setLoading(false);
    }
  }, [companyId, ownerId, user?.uid, user?.email, user?.firstName, user?.lastName, user?.createdAt, user?.status]);

  const fetchEmployeesWithAnimation = useCallback(async () => {
    setIsRefreshing(true);
    await fetchEmployees();
    setTimeout(() => setIsRefreshing(false), 800);
  }, [fetchEmployees]);

  useEffect(() => {
    if (companyId && ownerId && user?.uid) {
      fetchEmployees();
    } else {
      setLoading(false);
    }
  }, [companyId, ownerId, user?.uid, fetchEmployees]);

  const getRoleColor = (role) => {
    switch ((role || '').toLowerCase()) {
      case 'owner':
        return '#d32f2f';
      case 'manager':
        return '#ed6c02';
      case 'employee':
        return '#0288d1';
      default:
        return '#2e7d32';
    }
  };

  const roleFilterOptions = useMemo(() => {
    const ownerC = employees.filter((e) => isOwnerRole(e.role)).length;
    const managerC = employees.filter((e) => isManagerRole(e.role)).length;
    const employeeC = Math.max(0, employees.length - ownerC - managerC);
    return [
      { id: 'all', label: 'All', count: employees.length },
      { id: 'owner', label: 'Owner', count: ownerC },
      { id: 'manager', label: 'Manager', count: managerC },
      { id: 'employee', label: 'Employee', count: employeeC },
    ];
  }, [employees]);

  const teamMembersCountDisplay = useMemo(() => {
    const n = employees.length;
    const capacity = getEmployeeCapacity(companyData, n);
    if (capacity.unlimited) {
      return `${n} ${n === 1 ? 'employee' : 'employees'}`;
    }
    return `${n} / ${capacity.max}`;
  }, [companyData, employees.length]);

  const employeeLimitReached = useMemo(() => {
    return !canAddResource(companyData, 'employee', employees.length);
  }, [companyData, employees.length]);

  const canInviteEmployees = canCreateEmployee && !loading && !employeeLimitReached;

  const filteredEmployees = useMemo(() => {
    return employees
      .filter((employee) => {
        const matchesSearch =
          !searchTerm ||
          employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

        let matchesRole = true;
        if (roleFilterId === 'owner') matchesRole = isOwnerRole(employee.role);
        else if (roleFilterId === 'manager') matchesRole = isManagerRole(employee.role);
        else if (roleFilterId === 'employee') {
          matchesRole = !isOwnerRole(employee.role) && !isManagerRole(employee.role);
        }

        return matchesSearch && matchesRole;
      })
      .sort((a, b) => {
        if (a.id === user.uid) return -1;
        if (b.id === user.uid) return 1;
        if (isOwnerRole(a.role)) return -1;
        if (isOwnerRole(b.role)) return 1;

        let comparison = 0;
        switch (sortBy) {
          case 'name':
            comparison = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
            break;
          case 'role': {
            const roleOrder = { owner: 0, manager: 1, employee: 2 };
            const ar = (a.role || 'employee').toLowerCase();
            const br = (b.role || 'employee').toLowerCase();
            comparison = (roleOrder[ar] ?? 999) - (roleOrder[br] ?? 999);
            break;
          }
          case 'date':
            comparison = (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
            break;
          default:
            comparison = 0;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [employees, searchTerm, roleFilterId, sortBy, sortDirection, user.uid]);

  useEffect(() => {
    const pc =
      filteredEmployees.length === 0 ? 1 : Math.ceil(filteredEmployees.length / itemsPerPage);
    setTotalPages(Math.max(1, pc));
  }, [filteredEmployees.length, itemsPerPage]);

  useEffect(() => {
    if (currentPage > Math.max(1, Math.ceil(filteredEmployees.length / itemsPerPage))) {
      setCurrentPage(1);
    }
  }, [filteredEmployees.length, itemsPerPage, currentPage]);

  const paginatedEmployees = useMemo(() => {
    return filteredEmployees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredEmployees, currentPage, itemsPerPage]);

  const cssVars = useMemo(() => {
    const isDark = mode === 'dark';
    const bg = isDark ? '#0b1220' : '#f6f8fc';
    const surface = isDark ? '#0f172a' : '#ffffff';
    const surface2 = isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.7)';
    const text = isDark ? '#e5e7eb' : '#0f172a';
    const muted = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
    const shadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2, 6, 23, 0.08)';
    const shimmerA = isDark ? 'rgba(226, 232, 240, 0.08)' : 'rgba(15, 23, 42, 0.06)';
    const shimmerB = isDark ? 'rgba(226, 232, 240, 0.14)' : 'rgba(15, 23, 42, 0.12)';

    return {
      '--as-bg': bg,
      '--as-surface': surface,
      '--as-surface-2': surface2,
      '--as-text': text,
      '--as-muted': muted,
      '--as-border': border,
      '--as-shadow': shadow,
      '--as-primary': '#488aec',
      '--as-shimmer-a': shimmerA,
      '--as-shimmer-b': shimmerB,
    };
  }, [mode]);

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee || !canDeleteEmployee) return;

    try {
      await deleteEmployee({
        companyId: companyData.id,
        id: selectedEmployee.id,
      });

      const remaining = employees.filter((emp) => emp.id !== selectedEmployee.id);
      const company = await patchCompany({
        companyId: companyData.id,
        company: {
          resourceCounts: {
            employees: remaining.length,
            employeesCount: remaining.length,
          },
          employeesCount: remaining.length,
        },
      });
      setEmployees(remaining);
      updateCompanyData?.({ ...companyData, ...company });

      setNotification({
        open: true,
        message: 'Employee deleted successfully',
        severity: 'success',
      });
      setOpenDeleteDialog(false);
      setSelectedEmployee(null);
    } catch (err) {
      console.error('Error deleting employee:', err);
      setNotification({
        open: true,
        message: err.message || 'Failed to delete employee. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleEditEmployee = (employee) => {
    if (!canEditEmployee) return;
    navigate(`/app/overview/employees/profile/${getCompanyEmployeeUrlSegment(employee)}`);
  };

  const handleViewProfile = (employeeOrId) => {
    const employee =
      typeof employeeOrId === 'string'
        ? employees.find((entry) => entry.id === employeeOrId)
        : employeeOrId;
    const segment = employee
      ? getCompanyEmployeeUrlSegment(employee)
      : String(employeeOrId || '');
    navigate(`/app/overview/employees/profile/${segment}`);
  };

  const exportToCSV = () => {
    if (!canExport) return;
    const headers = ['First Name', 'Last Name', 'Email', 'Role', 'Join Date'];
    const csvData = employees.map((emp) => [
      emp.firstName,
      emp.lastName,
      emp.email,
      emp.role,
      emp.createdAt?.seconds ? format(new Date(emp.createdAt.seconds * 1000), 'yyyy-MM-dd') : 'N/A',
    ]);

    const csvContent = [headers.join(','), ...csvData.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `employees_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const formatJoinedDate = (timestamp) => {
    if (!timestamp) return 'Recently joined';
    if (timestamp.seconds) {
      return format(new Date(timestamp.seconds * 1000), 'MMM d, yyyy');
    }
    if (timestamp instanceof Date) {
      return format(timestamp, 'MMM d, yyyy');
    }
    return 'Recently joined';
  };

  const handleRoleChipClick = (id) => {
    setRoleFilterId(id);
    setCurrentPage(1);
  };

  const openMobileRoleFilter = useCallback(() => {
    setMobileSortOpen(false);
    setMobileRoleFilterOpen(true);
  }, []);

  const openMobileSort = useCallback(() => {
    setMobileRoleFilterOpen(false);
    setMobileSortOpen(true);
  }, []);

  const handleMobileSortChange = useCallback(
    (newSortBy) => {
      if (sortBy === newSortBy) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(newSortBy);
        setSortDirection('asc');
      }
      setMobileSortOpen(false);
    },
    [sortBy],
  );

  const handleViewModeChange = (modeName) => {
    setViewMode(modeName);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm !== '' || roleFilterId !== 'all';

  if (!user || !companyData) {
    return (
      <div className="employee-mgmt-loading" style={cssVars}>
        <div className="employee-mgmt-spinner" aria-label="Loading" />
      </div>
    );
  }

  if (!canViewEmployees) {
    return (
      <div className="employee-mgmt-denied" style={cssVars}>
        You don&apos;t have permission to view this page.
      </div>
    );
  }

  return (
    <div
      className={
        'employee-management-content' + (isSmDown ? ' employee-management-content--mobile-nav' : '')
      }
      style={cssVars}
    >
      <div className="management-split">
        <aside className="management-split__filters">
          <EmployeeSearchHeader
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onInviteEmployee={() => navigate('/app/overview/company-management/invite')}
            canInviteEmployee={canInviteEmployees}
            onOpenPendingInvites={() => setOpenPendingInvites(true)}
            onManagePermissions={() => navigate('/app/overview/permissions')}
            canOpenPermissions={hasPermission(PERMISSIONS.SETTINGS.EDIT)}
            onExport={exportToCSV}
            canExport={canExport}
            onRefresh={fetchEmployeesWithAnimation}
            isRefreshing={isRefreshing}
            isLoading={loading}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            teamMembersCountDisplay={teamMembersCountDisplay}
            mobileSeatPillLabel={teamMembersCountDisplay}
            roles={roleFilterOptions}
            selectedRoleId={roleFilterId}
            onRoleClick={handleRoleChipClick}
            hideToolbarActions={isSmDown}
            onOpenMobileRoleFilter={isSmDown ? openMobileRoleFilter : undefined}
            onOpenMobileSort={isSmDown ? openMobileSort : undefined}
          />
        </aside>

        <div className="management-split__content">
          {employeeLimitReached && (
            <div className="employee-mgmt-error" style={{ marginBottom: 16 }}>
              <p className="employee-mgmt-error-title">
                You&apos;ve reached your plan&apos;s employee limit. Upgrade your subscription to invite more team members.
              </p>
            </div>
          )}

          {error ? (
            <div className="employee-mgmt-error">
              <p className="employee-mgmt-error-title">{error}</p>
              <button type="button" className="employee-mgmt-btn" onClick={fetchEmployees}>
                Try Again
              </button>
            </div>
          ) : effectiveViewMode === 'gallery' ? (
            <EmployeeGallery
              employees={paginatedEmployees}
              onViewProfile={handleViewProfile}
              isLoading={loading}
              emptyStateMessage={
                hasActiveFilters ? 'Try adjusting search or role filters' : 'Invite your team to get started'
              }
              onInviteEmployee={
                canInviteEmployees ? () => navigate('/app/overview/company-management/invite') : undefined
              }
            />
          ) : (
            <EmployeeListView
              employees={paginatedEmployees}
              isLoading={loading}
              onViewProfile={handleViewProfile}
              onEdit={handleEditEmployee}
              onDelete={(employee) => {
                setSelectedEmployee(employee);
                setOpenDeleteDialog(true);
              }}
              getRoleColor={getRoleColor}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              formatDate={formatJoinedDate}
              canEdit={canEditEmployee}
              canDelete={canDeleteEmployee}
              companyData={companyData}
            />
          )}

          {totalPages > 1
            ? isSmDown
              ? (
                  <MobileManagementPagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalCount={filteredEmployees.length}
                    itemsPerPage={itemsPerPage}
                    resultsNoun="team members"
                  />
                )
              : (
                  <div
                    className={`management-pagination-bar client-management-pagination${isDarkMode ? ' is-dark' : ''}`}
                  >
                    <MobileManagementPagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      totalCount={filteredEmployees.length}
                      itemsPerPage={itemsPerPage}
                      resultsNoun="team members"
                      desktopBar
                    />
                  </div>
                )
            : null}
        </div>
      </div>

      <PendingInvitesDialog
        open={openPendingInvites}
        onClose={() => setOpenPendingInvites(false)}
        companyData={companyData}
      />

      {openDeleteDialog ? (
        <div
          className="employee-mgmt-dialog-backdrop"
          role="presentation"
          onClick={() => {
            setOpenDeleteDialog(false);
            setSelectedEmployee(null);
          }}
        >
          <div
            className="employee-mgmt-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-delete-title"
            onClick={(e) => e.stopPropagation()}
            style={cssVars}
          >
            <h2 id="employee-delete-title" className="employee-mgmt-dialog-title">
              Delete Employee
            </h2>
            <p className="employee-mgmt-dialog-body">
              Are you sure you want to delete {selectedEmployee?.firstName} {selectedEmployee?.lastName}? This action
              cannot be undone.
            </p>
            <div className="employee-mgmt-dialog-actions">
              <button
                type="button"
                className="employee-mgmt-btn employee-mgmt-btn--ghost"
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setSelectedEmployee(null);
                }}
              >
                Cancel
              </button>
              <button type="button" className="employee-mgmt-btn employee-mgmt-btn--danger" onClick={handleDeleteEmployee}>
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isSmDown ? (
        <EmployeeMobileFilterDrawers
          filterOpen={mobileRoleFilterOpen}
          onCloseFilter={() => setMobileRoleFilterOpen(false)}
          sortOpen={mobileSortOpen}
          onCloseSort={() => setMobileSortOpen(false)}
          roles={roleFilterOptions}
          selectedRoleId={roleFilterId}
          onRoleSelect={handleRoleChipClick}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleMobileSortChange}
        />
      ) : null}

      {isSmDown ? (
        <EmployeeManagementBottomNav
          onRefresh={fetchEmployeesWithAnimation}
          isRefreshing={isRefreshing}
          onInvite={() => navigate('/app/overview/company-management/invite')}
          canInvite={canInviteEmployees}
          onPendingInvites={() => setOpenPendingInvites(true)}
          onPermissions={() => navigate('/app/overview/permissions')}
          canOpenPermissions={hasPermission(PERMISSIONS.SETTINGS.EDIT)}
          onExport={exportToCSV}
          canExport={canExport}
        />
      ) : null}

    </div>
  );
}
